
import { defineComponent, inject } from 'vue'
import Checkbox from '@/components/shared/Checkbox.vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { SupplierService } from '@/services/supplier.service'
import { Contact } from '@/@types/responses/contact/Contact'
import ContactModal from '@/components/modals/ContactModal.vue'
import Toaster from '@meforma/vue-toaster'
import { SupplierDetails } from '@/@types/responses/supplier/SupplierDetails'
import ContactTile from '@/components/elements/ContactTile.vue'
import { ContactService } from '@/services/contact.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { InventoryTypeService } from '@/services/inventoryType.service'
import { ChecklistOptionResponse } from '@/@types/responses/common/ChecklistOptionResponse'
import { SupplierMessage } from '@/@types/messages/SupplierMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { StateService } from '@/services/states.service'

export default defineComponent({
  name: 'Supplier',
  components: {
    AuditInformation,
    Checkbox,
    ContactModal,
    ContactTile,
    Dropdown,
    LoadingPulse,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showContactModal) {
      this.showContactModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const contactService = inject(PROVIDER_CONSTANTS.contactService) as ContactService
    const inventoryTypeService = inject(PROVIDER_CONSTANTS.inventoryTypeService) as InventoryTypeService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      contactService,
      inventoryTypeService,
      stateService,
      supplierService,
      toast
    }
  },
  data() {
    let contactToEdit: Contact|undefined
    let supplier: SupplierDetails|undefined
    let unchangedSupplier: SupplierDetails|undefined

    return {
      contactToEdit,
      inventoryTypeChecklistOptions: [] as ChecklistOptionResponse<number>[],
      loading: true,
      saving: false,
      showContactModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      supplier: supplier,
      unchangedSupplier: unchangedSupplier
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.supplier
      const b = this.unchangedSupplier

      let inventoryTypesDifferent = false
      if (a?.inventoryTypeIds && b?.inventoryTypeIds) {
        const currentValues = a.inventoryTypeIds.sort((a, b) => a - b)
        const oldValues = b.inventoryTypeIds.sort((a, b) => a - b)

        for (let i = 0; i < currentValues.length; i++) {
          if (currentValues[i] !== oldValues[i]) {
            inventoryTypesDifferent = true
            break
          }
        }
      }

      return (a?.name !== b?.name ||
             a?.website !== b?.website ||
             a?.email !== b?.email ||
             a?.phoneNumber !== b?.phoneNumber ||
             a?.addressLine1 !== b?.addressLine1 ||
             a?.addressLine2 !== b?.addressLine2 ||
             a?.city !== b?.city ||
             a?.stateId !== b?.stateId ||
             a?.postalCode !== b?.postalCode ||
             inventoryTypesDifferent) && a?.name.trim() !== ''
    },
    titleText(): string {
      return !this.supplier || this.supplier.id === 0 ? 'Create Supplier' : 'Edit Supplier'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getStates().then(() => {
      if (this.id > 0) {
        this.getSupplier()
      } else {
        this.supplier = new SupplierDetails(0)
        this.unchangedSupplier = new SupplierDetails(0)
        this.getInventoryTypes().then(() => {
          this.loading = false
        })
      }
    })
  },
  methods: {
    getInventoryTypes(): Promise<void> {
      return this.inventoryTypeService.getInventoryTypeDropdownOptions(undefined).then(dropdownOptionsResponse => {
        const checklistOptions = [] as ChecklistOptionResponse<number>[]
        dropdownOptionsResponse.dropdownOptions.forEach(dropdownOption => {
          let checked = false
          if (this.supplier)
            checked = this.supplier.inventoryTypeIds.includes(dropdownOption.id as number)

          checklistOptions.push(new ChecklistOptionResponse<number>(dropdownOption.id, dropdownOption.name, checked))
        })
        this.inventoryTypeChecklistOptions = checklistOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the build types. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    getSupplier(): Promise<void> {
      return this.supplierService.getSupplier(this.supplier ? this.supplier.id : this.id).then(supplier => {
        this.supplier = supplier
        this.unchangedSupplier = JSON.parse(JSON.stringify(supplier))
        this.updatePageTitle()
        this.getInventoryTypes().then(() => {
          this.loading = false
        })
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the supplier. Try reloading the page.')
        }
      })
    },
    onCreateContactClicked(): void {
      this.contactToEdit = new Contact(0)
      this.showContactModal = true
    },
    onDeleteContact(contact: Contact): void {
      this.contactService.deleteContact(contact.id).then(() => {
        this.getSupplier().then(() => {
          this.toast.success('The contact was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the contact. Please try again.')
        }
      })
    },
    onEditContact(contact: Contact): void {
      this.contactToEdit = contact
      this.showContactModal = true
    },
    onInventoryTypeChanged(inventoryType: ChecklistOptionResponse<number>): void {
      if (!this.supplier) return

      if (inventoryType.checked) {
        this.supplier.inventoryTypeIds.push(inventoryType.id as number)
      } else {
        const index = this.supplier.inventoryTypeIds.findIndex(i => i === inventoryType.id as number)
        if (index === -1) return

        this.supplier.inventoryTypeIds.splice(index, 1)
      }
    },
    onSaveSupplierClicked(): void {
      if (!this.supplier) return

      this.saving = true

      const supplierMessage = new SupplierMessage(this.supplier.name, this.supplier.website, this.supplier.email, this.supplier.phoneNumber, this.supplier.addressLine1, this.supplier.addressLine2, this.supplier.city, this.supplier.stateId, this.supplier.postalCode, this.supplier.inventoryTypeIds)
      if (this.supplier.id === 0)
        this.supplierService.createSupplier(supplierMessage).then(objectCreatedResponse => {
          if (!this.supplier) return
          
          this.supplier.id = objectCreatedResponse.id
          this.getSupplier().then(() => {
            this.$router.replace(`/supplier/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The supplier was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the supplier. Please try again.')
          }
        })
      else
        this.supplierService.updateSupplier(this.id, supplierMessage).then(() => {
          this.toast.success('The supplier was updated successfully.')
          this.saving = false
          this.$router.push('/suppliers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the supplier. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.supplier)
        document.title = `${this.supplier.name} - Short Action Customs, LLC`
    }
  }
})
